import { motion } from 'framer-motion';
import { Check, Shield, Users, Star } from 'lucide-react';
import { useState } from 'react';

const PricingLight = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const subscriptions = [
    {
      name: 'Protego Monitor',
      monthlyPrice: 297,
      yearlyPrice: 2970,
      yearlyNote: '2 months free',
      description: 'Continuous monitoring of your Amazon account across 30 behavioral signals, six KPI domains, and one Enforcement Risk Score.',
      features: [
        '30 behavioral signals monitored',
        'Six KPI domains tracked',
        'Enforcement Risk Score updates',
        'Unlimited specialist walkthrough calls',
        'Plan of Action generation included',
        'Downloadable audit report anytime',
        '30-day money-back guarantee'
      ],
      icon: Shield,
      popular: true,
      cta: 'Start Monitoring',
      ctaLinkMonthly: 'https://buy.stripe.com/7sY3cv47V82W7ZjbBv5os04',
      ctaLinkYearly: 'https://buy.stripe.com/cNifZh0VJcjccfzaxr5os03'
    },
    {
      name: 'Protego Guard',
      monthlyPrice: 697,
      yearlyPrice: null,
      yearlyNote: 'Monthly only',
      description: 'Everything in Monitor, plus a dedicated account health specialist in a private Slack channel.',
      features: [
        'Everything in Monitor',
        'Dedicated account health specialist',
        'Private Slack channel access',
        'Human review when risk score moves',
        'Triage and guidance',
        'Priority response'
      ],
      icon: Users,
      popular: false,
      cta: 'Get Protected',
      ctaLinkMonthly: 'https://buy.stripe.com/3cI14ncErbf8enHgVP5os00',
      ctaLinkYearly: null
    }
  ];

  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="pricing-section">
      <div className="max-w-5xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-purple-600">
            After the Workshop
          </p>
          <h2 className="font-display text-slate-800 text-3xl sm:text-4xl lg:text-5xl mt-3">
            Ready for Continuous Protection?
          </h2>
          <p className="text-slate-500 text-lg mt-4 max-w-2xl mx-auto">
            Start with the free workshop, then choose the protection level that fits your business.
          </p>
        </motion.div>

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center justify-center gap-4 mb-10"
        >
          <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-slate-800' : 'text-slate-400'}`}>Monthly</span>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
            className="relative w-14 h-7 rounded-full bg-slate-200 transition-colors"
            data-testid="billing-toggle"
          >
            <div className={`absolute top-1 w-5 h-5 rounded-full bg-purple-600 transition-transform ${billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
          <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-slate-800' : 'text-slate-400'}`}>
            Yearly <span className="text-purple-600">(Save 2 months)</span>
          </span>
        </motion.div>

        {/* Subscription Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {subscriptions.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative bg-white rounded-2xl p-8 border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                plan.popular 
                  ? 'border-purple-500 shadow-lg shadow-purple-500/10' 
                  : 'border-slate-200 hover:border-purple-300'
              }`}
              data-testid={`pricing-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-purple-600 rounded-full text-xs font-semibold text-white flex items-center gap-1">
                  <Star className="w-3 h-3" /> Most Popular
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl ${plan.popular ? 'bg-purple-600' : 'bg-purple-100'} flex items-center justify-center`}>
                  <plan.icon className={`w-6 h-6 ${plan.popular ? 'text-white' : 'text-purple-600'}`} />
                </div>
                <h3 className="text-2xl font-display text-slate-800">{plan.name}</h3>
              </div>

              <div className="mb-4">
                {billingCycle === 'yearly' && plan.yearlyPrice ? (
                  <>
                    <span className="text-4xl font-display text-slate-800">~${Math.round(plan.yearlyPrice / 12)}</span>
                    <span className="text-slate-500 ml-2">/month</span>
                    <p className="text-sm text-purple-600 mt-1">${plan.yearlyPrice.toLocaleString()}/year — {plan.yearlyNote}</p>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-display text-slate-800">${plan.monthlyPrice}</span>
                    <span className="text-slate-500 ml-2">/month</span>
                    {plan.yearlyPrice ? (
                      <p className="text-sm text-slate-500 mt-1">or ${plan.yearlyPrice.toLocaleString()}/year ({plan.yearlyNote})</p>
                    ) : (
                      <p className="text-sm text-slate-500 mt-1">{plan.yearlyNote}</p>
                    )}
                  </>
                )}
              </div>

              <p className="text-slate-500 text-sm mb-6">{plan.description}</p>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                    <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href={billingCycle === 'yearly' && plan.ctaLinkYearly ? plan.ctaLinkYearly : plan.ctaLinkMonthly}
                target="_blank"
                rel="noopener noreferrer"
                className={`block w-full py-4 px-6 text-center font-semibold rounded-xl transition-all ${
                  plan.popular 
                    ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
                }`}
              >
                {plan.cta}
              </a>

              {plan.popular && (
                <p className="text-center text-sm text-slate-500 mt-4">30-day money-back guarantee</p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingLight;
